import React, { useCallback, useMemo, useState } from 'react';
import {
    Box,
    TextField,
    Paper,
    Grid,
    MenuItem,
} from '@mui/material';
import { useQuery } from 'react-query';
import MultiDateSelector from './MultiDateSelector';
import { dateUtils } from '../../../utils/DateUtils';
import { usePropertyId } from '../../../common/hooks/usePropertyId';

/**
 * @callback GetAvailableCheckinDates
 * @returns {Promise<import('../../../utils/availabilityStatus').AvailabilityByDateMap>}
 *   Map of ISO date → check-in availability status (checkinAvailability).
 */

/**
 * @callback GetAvailableCheckoutDates
 * @param {Date} checkinDate — selected arrival (local calendar date).
 * @returns {Promise<import('../../../utils/availabilityStatus').AvailabilityByDateMap>}
 *   Map of ISO date → check-out availability status (checkoutAvailability).
 */

const DatesSelector = ({
    checkin,
    checkout,
    adults,
    onDateChange,
    onAdultsChange,
    minDate,
    disabled,
    /** @type {GetAvailableCheckinDates|undefined} */
    getAvailableCheckinDates,
    /** @type {GetAvailableCheckoutDates|undefined} */
    getAvailableCheckoutDates,
}) => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const propertyId = usePropertyId();

    const handleVisibleMonthsChange = useCallback(({ open }) => {
        setCalendarOpen(Boolean(open));
    }, []);

    const checkinAvailabilityQuery = useQuery({
        queryKey: ['availableCheckinDates', propertyId],
        queryFn: () => getAvailableCheckinDates(),
        enabled: Boolean(getAvailableCheckinDates) && calendarOpen,
    });

    const checkoutAvailabilityQuery = useQuery({
        queryKey: ['availableCheckoutDates', propertyId, checkin ? dateUtils.getDateString(checkin) : ''],
        queryFn: () => getAvailableCheckoutDates(checkin),
        enabled:
            Boolean(getAvailableCheckoutDates) &&
            Boolean(checkin) &&
            !checkout &&
            calendarOpen,
        cacheTime: 0,
        staleTime: 0,
    });

    const isCheckinAvailabilityLoading = Boolean(
        getAvailableCheckinDates &&
            calendarOpen &&
            (checkinAvailabilityQuery.isLoading || checkinAvailabilityQuery.isFetching)
    );

    const isCheckoutAvailabilityLoading = Boolean(
        getAvailableCheckoutDates &&
            checkin &&
            !checkout &&
            calendarOpen &&
            (checkoutAvailabilityQuery.isLoading || checkoutAvailabilityQuery.isFetching)
    );

    const checkinAvailabilityByDate = useMemo(() => {
        const data = checkinAvailabilityQuery.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            return data;
        }
        return undefined;
    }, [checkinAvailabilityQuery.data]);

    const checkoutAvailabilityByDate = useMemo(() => {
        const data = checkoutAvailabilityQuery.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            return data;
        }
        return undefined;
    }, [checkoutAvailabilityQuery.data]);

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={6}>
                        <MultiDateSelector
                            checkin={checkin}
                            checkout={checkout}
                            onChange={onDateChange}
                            minDate={minDate}
                            disabled={disabled || isCheckinAvailabilityLoading || isCheckoutAvailabilityLoading}
                            onVisibleMonthsChange={handleVisibleMonthsChange}
                            checkinAvailabilityByDate={
                                getAvailableCheckinDates ? checkinAvailabilityByDate : undefined
                            }
                            checkoutAvailabilityByDate={
                                getAvailableCheckoutDates && checkin && !checkout
                                    ? checkoutAvailabilityByDate
                                    : undefined
                            }
                            isCheckinAvailabilityLoading={isCheckinAvailabilityLoading}
                            isCheckoutAvailabilityLoading={isCheckoutAvailabilityLoading}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label="Dorośli"
                            value={adults}
                            onChange={(e) => onAdultsChange(parseInt(e.target.value, 10))}
                            size="small"
                            fullWidth
                            disabled={disabled}
                        >
                            {[1, 2, 3, 4, 5, 6].map((num) => (
                                <MenuItem key={num} value={num}>
                                    {num} {num === 1 ? 'dorosły' : 'dorosłych'}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default DatesSelector;
